/**
 * Content 路由上下文快照管理工具。
 *
 * 作用：
 * 1. 使用 routeContextKey 将完整 routeInfo 保存到当前标签页的 sessionStorage。
 * 2. ContentPage、ContentView、Header 和 Nav 通过同一个 key 读取独立快照，避免多标签串页。
 * 3. URL 只保留 routeContextKey；sqm_header_button_id、sqm_side_menu_id、sqm_header_nav 等菜单字段保留在 routeInfo 快照内，避免重复暴露在地址栏。
 * 4. 校验快照结构和 ROUTE_CONTEXT_MAX_AGE 配置的有效期，自动删除损坏或过期数据。
 * 5. 兼容历史 raw routeInfo 格式，旧快照的创建时间从 routeContextKey 前缀获取。
 *
 * 当前快照格式：
 * {
 *   createdAt: 1780000000000,
 *   routeInfo: {
 *     view: 'iframe 页面地址',
 *     roleCode: 'S006',
 *     sqm_header_button_id: 87,
 *     sqm_header_nav: '/newproduct/appeal_model'
 *   }
 * }
 */

// routeInfo 快照在 sessionStorage 中使用的 key 前缀。
const ROUTE_CONTEXT_PREFIX = 'sqm_route_info:';

// 失效提示时间记录的 key 前缀，用于防止组件初始化阶段重复弹出相同提示。
const ROUTE_CONTEXT_NOTICE_PREFIX = 'sqm_route_context_invalid_notified:';

// 同一个失效 key 在该时间内只提示一次，单位：毫秒。
const ROUTE_CONTEXT_NOTICE_INTERVAL = 2000;

// 路由上下文最大有效期，所有过期判断统一使用该常量；当前配置为 12 小时，单位：毫秒。
export const ROUTE_CONTEXT_MAX_AGE = 12 * 60 * 60 * 1000;

/**
 * 判断数据是否为非数组对象。
 *
 * @param {*} value 待判断的数据。
 * @returns {boolean} 是否为可用对象。
 */
const isObject = (value) => {
  return value && typeof value === 'object' && !Array.isArray(value);
};

/**
 * 判断数据是否为可用的 routeInfo。
 * routeInfo 必须是对象，并且必须包含非空字符串 view，防止生成 undefined iframe 地址。
 *
 * @param {*} value 待判断的路由上下文数据。
 * @returns {boolean} 是否为有效 routeInfo。
 */
const isRouteInfo = (value) => {
  return isObject(value) && typeof value.view === 'string' && value.view.trim() !== '';
};

/**
 * 从旧版 routeContextKey 中提取快照创建时间。
 * 旧 key 格式：<时间戳>_<随机字符串>，例如 1780000000000_abcd1234。
 *
 * @param {string} routeContextKey 路由上下文唯一标识。
 * @returns {number|null} 创建时间戳；无法解析时返回 null。
 */
const getLegacyCreatedAt = (routeContextKey) => {
  const timestamp = Number(String(routeContextKey).split('_')[0]);
  return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : null;
};

/**
 * 获取 routeInfo 快照在 sessionStorage 中的完整存储 key。
 *
 * @param {string} routeContextKey URL 中携带的路由上下文唯一标识。
 * @returns {string} sessionStorage key，例如 sqm_route_info:1780000000000_abcd1234。
 */
export const getRouteContextStorageKey = (routeContextKey) => {
  return `${ROUTE_CONTEXT_PREFIX}${routeContextKey}`;
};

/**
 * 保存路由上下文快照。
 * 新建页面时 createdAt 使用当前时间；更新角色信息时传入原 createdAt，避免重新计算有效期。
 *
 * @param {string} routeContextKey 路由上下文唯一标识。
 * @param {Object} routeInfo 完整路由参数，必须包含非空 view。
 * @param {number} [createdAt=Date.now()] 快照最初创建时间，单位：毫秒。
 * @returns {boolean} 是否保存成功。
 */
export const saveRouteContextInfo = (routeContextKey, routeInfo, createdAt = Date.now()) => {
  const normalizedCreatedAt = Number(createdAt);
  if (
    !routeContextKey
    || !isRouteInfo(routeInfo)
    || !Number.isFinite(normalizedCreatedAt)
    || Date.now() - normalizedCreatedAt >= ROUTE_CONTEXT_MAX_AGE
  ) {
    return false;
  }

  // 每次写入前清理其他过期或损坏快照，防止长期不访问的旧 key 持续占用空间。
  cleanupExpiredRouteContexts();

  sessionStorage.setItem(getRouteContextStorageKey(routeContextKey), JSON.stringify({
    createdAt: normalizedCreatedAt,
    routeInfo
  }));
  return true;
};

/**
 * 读取并校验完整路由上下文快照。
 *
 * 校验规则：
 * 1. key 对应数据必须存在且可以解析为 JSON。
 * 2. routeInfo 必须包含非空 view。
 * 3. createdAt 必须有效，且快照未超过 ROUTE_CONTEXT_MAX_AGE 配置的有效期。
 * 4. 损坏或过期数据会立即从 sessionStorage 删除。
 * 5. 旧 raw routeInfo 格式使用 routeContextKey 前缀时间戳作为 createdAt。
 *
 * @param {string} routeContextKey 路由上下文唯一标识。
 * @returns {{createdAt: number, routeInfo: Object}|null} 有效快照；缺失、损坏或过期时返回 null。
 */
export const getRouteContextSnapshot = (routeContextKey) => {
  if (!routeContextKey) {
    return null;
  }

  const storageKey = getRouteContextStorageKey(routeContextKey);
  const savedRouteContext = sessionStorage.getItem(storageKey);
  if (!savedRouteContext) {
    return null;
  }

  try {
    const parsedRouteContext = JSON.parse(savedRouteContext);
    // 只要存在 routeInfo 字段，就按新格式严格校验，防止损坏的新格式被误判为旧格式。
    const isCurrentFormat = isObject(parsedRouteContext)
      && Object.prototype.hasOwnProperty.call(parsedRouteContext, 'routeInfo');
    const routeInfo = isCurrentFormat ? parsedRouteContext.routeInfo : parsedRouteContext;
    const createdAt = isCurrentFormat
      ? Number(parsedRouteContext.createdAt)
      : getLegacyCreatedAt(routeContextKey);

    if (!isRouteInfo(routeInfo) || !Number.isFinite(createdAt) || Date.now() - createdAt >= ROUTE_CONTEXT_MAX_AGE) {
      sessionStorage.removeItem(storageKey);
      return null;
    }

    return { createdAt, routeInfo };
  } catch {
    sessionStorage.removeItem(storageKey);
    return null;
  }
};

/**
 * 全量清理当前标签页中已过期或损坏的 routeContext 快照。
 * 每个快照都通过 getRouteContextSnapshot 校验，因此有效期统一使用 ROUTE_CONTEXT_MAX_AGE。
 * 此方法只处理 sqm_route_info: 前缀的 namespaced 快照，不影响旧版 sqm_route_info 和其他业务数据。
 *
 * @returns {number} 本次删除的快照数量。
 */
export function cleanupExpiredRouteContexts() {
  const routeContextStorageKeys = Object.keys(sessionStorage)
    .filter((storageKey) => storageKey.startsWith(ROUTE_CONTEXT_PREFIX));
  let removedCount = 0;

  routeContextStorageKeys.forEach((storageKey) => {
    const routeContextKey = storageKey.slice(ROUTE_CONTEXT_PREFIX.length);
    const savedRouteContext = sessionStorage.getItem(storageKey);
    getRouteContextSnapshot(routeContextKey);

    if (savedRouteContext !== null && sessionStorage.getItem(storageKey) === null) {
      removedCount += 1;
    }
  });

  return removedCount;
}

/**
 * 只获取有效快照中的 routeInfo。
 * Content、ContentPage、ContentView 和 Nav 的普通读取场景使用此方法。
 *
 * @param {string} routeContextKey 路由上下文唯一标识。
 * @returns {Object|null} 有效 routeInfo；快照无效时返回 null。
 */
export const getRouteContextInfo = (routeContextKey) => {
  return getRouteContextSnapshot(routeContextKey)?.routeInfo || null;
};

/**
 * 更新已有 routeInfo，同时保留快照最初的 createdAt。
 * 主要用于角色切换后更新 roleCode、roleId、roleName、status、type，更新不会延长原有效期。
 *
 * @param {string} routeContextKey 路由上下文唯一标识。
 * @param {Object} routeInfo 更新后的完整路由参数，必须包含非空 view。
 * @returns {boolean} 更新是否成功；原快照缺失、损坏或过期时返回 false。
 */
export const updateRouteContextInfo = (routeContextKey, routeInfo) => {
  const routeContextSnapshot = getRouteContextSnapshot(routeContextKey);
  if (!routeContextSnapshot) {
    return false;
  }

  return saveRouteContextInfo(routeContextKey, routeInfo, routeContextSnapshot.createdAt);
};

/**
 * 判断本次是否需要显示“页面上下文已失效”提示。
 * 同一个 key 在 2 秒内只允许提示一次，防止 ContentPage/ContentView 初始化时重复弹窗；
 * 超过 2 秒后再次访问同一失效链接，仍会重新提示。
 *
 * @param {string} routeContextKey 已失效的路由上下文唯一标识。
 * @returns {boolean} true 表示本次应显示提示；false 表示应抑制重复提示。
 */
export const shouldNotifyRouteContextInvalid = (routeContextKey) => {
  if (!routeContextKey) {
    return false;
  }

  const noticeKey = `${ROUTE_CONTEXT_NOTICE_PREFIX}${routeContextKey}`;
  const currentTime = Date.now();
  const lastNoticeTime = Number(sessionStorage.getItem(noticeKey));
  if (Number.isFinite(lastNoticeTime) && currentTime - lastNoticeTime < ROUTE_CONTEXT_NOTICE_INTERVAL) {
    return false;
  }

  sessionStorage.setItem(noticeKey, String(currentTime));
  return true;
};
